import { useState } from 'react';
import { X, Copy, Check, Loader2, AlertTriangle, Share2, Power, PowerOff } from 'lucide-react';
import useCategoryCopyCodes from '../../hooks/useCategoryCopyCodes';
import useEscapeClose from '../../hooks/useEscapeClose';

/**
 * Owner-facing modal to manage copy codes of a user_category.
 * Uses sem-* classes like SujetEditModal for consistency.
 */
// Date « 20 juin 2026 14:32 » pour le suivi des copies.
function fmtRedeemDate(iso) {
    if (!iso) return '';
    try {
        return new Date(String(iso).replace(' ', 'T')).toLocaleString('fr-FR', { day: 'numeric', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
    } catch { return iso; }
}

export default function CategoryCopyCodeModal({ categoryId, categoryLabel, onClose }) {
    const { items, loading, error, generate, revoke } = useCategoryCopyCodes(categoryId);
    const [busy, setBusy] = useState(false);
    const [copiedId, setCopiedId] = useState(null);
    const [localError, setLocalError] = useState(null);
    const [expandedId, setExpandedId] = useState(null);
    useEscapeClose(!busy, onClose);

    const handleGenerate = async () => {
        setBusy(true);
        setLocalError(null);
        try { await generate(); }
        catch (e) { setLocalError(e.response?.data?.message || e.message); }
        setBusy(false);
    };

    const handleRevoke = async (id, isActive) => {
        setBusy(true);
        setLocalError(null);
        try { await revoke(id, isActive); }
        catch (e) { setLocalError(e.response?.data?.message || e.message); }
        setBusy(false);
    };

    const copyToClipboard = (code, id) => {
        navigator.clipboard.writeText(code).then(() => {
            setCopiedId(id);
            setTimeout(() => setCopiedId(null), 1500);
        });
    };

    return (
        <div className="sem-overlay" onClick={onClose}>
            <div className="sem-modal" style={{ maxWidth: 560 }} onClick={(e) => e.stopPropagation()}>
                <div className="sem-header">
                    <Share2 size={16} />
                    <span className="sem-title">Codes de copie — {categoryLabel}</span>
                    <button className="sem-close" onClick={onClose} aria-label="Fermer"><X size={18} /></button>
                </div>

                <div className="sem-section" style={{ padding: '14px 18px' }}>
                    <p style={{ fontSize: '0.88em', color: 'var(--text-muted)', marginTop: 0, marginBottom: 12 }}>
                        Un code de copie permet à un autre utilisateur (avec un abonnement thématique actif) de dupliquer tous les sujets de cette catégorie dans l'une de ses thématiques. Le code est réutilisable par plusieurs personnes, mais chacun ne peut l'utiliser qu'une fois par thématique.
                    </p>

                    <button className="btn btn-primary" onClick={handleGenerate} disabled={busy}>
                        {busy ? <><Loader2 size={14} className="spin" /> Génération…</> : <><Share2 size={14} /> Générer un nouveau code</>}
                    </button>

                    {(localError || error) && (
                        <div className="sem-notice sem-notice--error" style={{ marginTop: 10 }}>
                            <AlertTriangle size={13} /> {localError || error}
                        </div>
                    )}

                    <div style={{ marginTop: 16 }}>
                        {loading && <div style={{ fontSize: '0.88em', color: 'var(--text-muted)' }}><Loader2 size={13} className="spin" /> Chargement…</div>}
                        {!loading && items.length === 0 && (
                            <div style={{ fontSize: '0.88em', color: 'var(--text-muted)', fontStyle: 'italic' }}>Aucun code généré pour l'instant.</div>
                        )}
                        {items.map((c) => (
                            <div key={c.id} style={{
                                border: '1px solid var(--border)', borderRadius: 6, marginBottom: 6,
                                background: c.is_active ? 'var(--surface, #fff)' : 'var(--bg-subtle, #f8fafc)',
                                opacity: c.is_active ? 1 : 0.6,
                            }}>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8, padding: '8px 10px' }}>
                                    <code style={{ fontSize: '0.92em', fontWeight: 600, letterSpacing: 0.5, flex: 1 }}>{c.code}</code>
                                    <button
                                        type="button"
                                        onClick={() => c.nb_redemptions > 0 && setExpandedId(expandedId === c.id ? null : c.id)}
                                        style={{
                                            background: 'none', border: 'none', padding: 0, fontSize: '0.78em',
                                            color: 'var(--text-muted)', minWidth: 90, textAlign: 'right',
                                            cursor: c.nb_redemptions > 0 ? 'pointer' : 'default',
                                            textDecoration: c.nb_redemptions > 0 ? 'underline' : 'none',
                                        }}
                                        title={c.nb_redemptions > 0 ? 'Voir qui a copié et quand' : undefined}
                                    >
                                        {c.nb_redemptions} copie{c.nb_redemptions > 1 ? 's' : ''}
                                    </button>
                                    <button
                                        className="sem-icon-btn"
                                        title="Copier dans le presse-papiers"
                                        onClick={() => copyToClipboard(c.code, c.id)}
                                    >
                                        {copiedId === c.id ? <Check size={13} style={{ color: '#10b981' }} /> : <Copy size={13} />}
                                    </button>
                                    <button
                                        className="sem-icon-btn"
                                        title={c.is_active ? 'Révoquer (les copies déjà faites restent)' : 'Réactiver'}
                                        onClick={() => handleRevoke(c.id, !c.is_active)}
                                        disabled={busy}
                                    >
                                        {c.is_active ? <PowerOff size={13} style={{ color: '#ef4444' }} /> : <Power size={13} style={{ color: '#10b981' }} />}
                                    </button>
                                </div>
                                {expandedId === c.id && (c.redemptions || []).length > 0 && (
                                    <div style={{ borderTop: '1px solid var(--border)', padding: '6px 10px', display: 'flex', flexDirection: 'column', gap: 4 }}>
                                        {c.redemptions.map((r, i) => (
                                            <div key={i} style={{ fontSize: '0.78em', color: 'var(--text-muted)', display: 'flex', gap: 6, flexWrap: 'wrap', alignItems: 'baseline' }}>
                                                <strong style={{ color: 'var(--text)' }}>{r.prenom || r.email}</strong>
                                                {r.prenom && r.email && <span>{r.email}</span>}
                                                <span>· {fmtRedeemDate(r.redeemed_at)}</span>
                                                <span>· {r.nb_sujets_copied} sujet{r.nb_sujets_copied > 1 ? 's' : ''}</span>
                                            </div>
                                        ))}
                                    </div>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            </div>
        </div>
    );
}
